import { useMutation } from "@tanstack/react-query";
import { apiClient } from "./api-client";

/**
 * What `POST /api/v1/email_subscribers` returns — the normalized address and
 * nothing else (contracts/email-subscribers-api.md §1).
 *
 * There is deliberately no `id`, no `created_at`, and no `created` flag: a
 * repeat signup answers byte-identically to a first one, so neither this type
 * nor the form that consumes it has anything to branch on and neither can
 * disclose prior membership (FR-003).
 */
export interface EmailSubscription {
  email: string;
}

/**
 * The site's one and only write: subscribe an address to the launch list.
 *
 * The value is sent verbatim. Normalization (`strip.downcase`) is the server's
 * job (FR-004), and putting a second normalizer here would only give it
 * something to drift from.
 *
 * No status is inspected and nothing is caught. The api-client throws
 * `HttpError` on any status >= 400, and a transport failure rejects on its own,
 * so every failure reaches the caller as a rejection — which is exactly what
 * puts the signup form in its retryable error state with the typed value
 * retained (acceptance scenario 1.4). Resolving a 4xx here would show a
 * confirmation to somebody who never got subscribed.
 */
export async function subscribeEmail(
  email: string,
): Promise<EmailSubscription> {
  const response = await apiClient.post<EmailSubscription>(
    "/api/v1/email_subscribers",
    { email },
  );
  return response.data;
}

/**
 * `subscribeEmail` as a TanStack mutation, giving the signup form its pending
 * and error states without hand-rolled state (contracts/web-surface.md §3).
 *
 * Retries are off: the endpoint is idempotent, so a retry is harmless on the
 * server, but a silent one would keep the form in its pending state well past
 * the point where the visitor deserves a retryable error to act on.
 */
export function useSubscribeEmail() {
  return useMutation({
    mutationFn: subscribeEmail,
    retry: false,
  });
}
